import { useMemo, VFC } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowActionMeta, WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionZendeskAddTagsToTicketV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchemaTypes } from 'lib/json-schema';

interface ParamsZendeskAddTagsToTicketV1Props extends ParamsBaseProps {
  externalIntegrations: WorkflowActionMeta['external-integrations'];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionZendeskAddTagsToTicketV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionZendeskAddTagsToTicketV1['params']) => void;
}

const ParamsZendeskAddTagsToTicketV1: VFC<ParamsZendeskAddTagsToTicketV1Props> = ({
  externalIntegrations,
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
}) => {
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const updateZendeskTicketParams = <
    Key extends keyof WorkflowActionZendeskAddTagsToTicketV1['params'],
  >(
    key: Key,
    value: WorkflowActionZendeskAddTagsToTicketV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const zendeskInstanceOptions = [
    ...externalIntegrations
      .filter(
        (integration) =>
          integration.type === ApiModel.ExternalIntegrationProductivityZendeskOAuth &&
          !integration.isArchived,
      )
      .map((integration) => ({
        label: integration.name,
        value: integration.token,
      })),
    { label: 'Add New Zendesk Instance', value: 'marketplace-managment-redirect' },
  ];

  const navigate = useNavigate();
  const selectZendeskInstance = (zendeskInstance: string) => {
    if (zendeskInstance === 'marketplace-managment-redirect') {
      navigate(`/dashboard/marketplace/zendesk`);
    } else {
      updateActionParams({
        ...params,
        'external-integration-id': zendeskInstance,
      });
    }
  };

  const tagOptions = (tagParam: string[]) => {
    return tagParam.map((tag) => {
      return { label: tag, value: tag };
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionZendeskAddTagsToTicketV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Zendesk Instance'
        htmlFor='params-zendesk-add-tags-to-ticket__external-integration-id'
      />
      <Select
        value={params['external-integration-id']}
        onValueChange={selectZendeskInstance}
        id='params-zendesk-add-tags-to-ticket__external-integration-id'
        disabled={!editable}
        hasError={hasErrorFor('external-integration-id')}
      >
        {zendeskInstanceOptions.map((opt) => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('external-integration-id')}
      <Spacer height={2} />
      <ParamLabel labelText='Ticket ID' htmlFor='params-zendesk-add-tags-to-ticket__ticket-id' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['ticket-id']}
        updateValue={(value: string) => {
          updateZendeskTicketParams('ticket-id', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-zendesk-add-tags-to-ticket__ticket-id'
        hasError={hasErrorFor('ticket-id')}
      />
      {renderErrorMessageFor('ticket-id')}
      <Spacer height={2} />
      <ParamLabel labelText='Tags' htmlFor='params-zendesk-add-tags-to-ticket__tags-input' />
      <ReactCreatableSelect
        size='md'
        id='params-zendesk-add-tags-to-ticket__tags'
        value={params['tags']}
        disabled={!editable}
        isMulti={true}
        isSearchable={true}
        portal={true}
        onChange={(value: string[]) => {
          updateZendeskTicketParams('tags', value);
        }}
        onCreateOption={(str: string) => {
          updateZendeskTicketParams('tags', params['tags'].concat(str));
        }}
        options={tagOptions(params['tags'])}
      />
      <Spacer height={2} />
    </>
  );
};
export default ParamsZendeskAddTagsToTicketV1;
